import { useState } from 'react';
import GreenPill from './GreenPill';
import { getTopUpPrices } from '~/utils/currency';

interface TopUpContentProps {
  token: string;
  paymentUrl: string;
  currency: 'USD' | 'INR';
  onContactSales?: () => void;
}

export default function TopUpContent({
  paymentUrl = 'https://payments.dualite.dev',
  token,
  currency,
  onContactSales,
}: TopUpContentProps) {
  // Get fixed prices for the selected currency
  const finalCurrency = currency === 'INR' ? 'INR' : 'USD';
  const prices = getTopUpPrices(finalCurrency);
  const currencySymbol = currency === 'INR' ? '₹' : '$';
  const [loadingButton, setLoadingButton] = useState<number | null>(null);

  const handlePurchase = (messages: number) => {
    setLoadingButton(messages);
    window.location.href = `${paymentUrl}/topup?token=${token}&currency=${finalCurrency}&selectedPlanName=${messages}`;
  };

  const handleContactClick = () => {
    if (onContactSales) {
      onContactSales();
    } else {
      window.open('https://calendar.app.google/sAggpvWcDjTWUasj9', '_blank');
    }
  };

  return (
    <div className="w-full bg-white px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8">
          {/* Badge */}
          <div className="flex justify-center mb-6">
            <GreenPill icon={<div className="i-ph-sparkle text-[#8DD290] w-4 h-4"></div>}>
              20% off on all top-ups
            </GreenPill>
          </div>

          <h1
            className="text-black text-4xl sm:text-5xl lg:text-6xl font-semibold mb-4"
            style={{ fontFamily: 'Inter Display' }}
          >
            Get{' '}
            <span className="font-regular italic" style={{ fontFamily: 'Instrument Serif' }}>
              Additional
            </span>{' '}
            Messages
          </h1>

          <p className="text-black/60 text-lg sm:text-xl" style={{ fontFamily: 'Inter Display' }}>
            Top up your message quota and keep building without interruption
          </p>
        </div>

        {/* Top Up Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-[1400px] mx-auto mb-8">
          {prices.map((plan) => (
            <div
              key={plan.messages}
              className={`bg-white backdrop-blur-sm rounded-xl p-6 flex flex-col border transition-all duration-300 relative overflow-visible shadow-lg ${
                plan.isPopular ? 'border-black hover:border-[#8DD290]/80' : 'border-black/10 hover:border-black/20'
              }`}
            >
              {/* Popular Badge */}
              {plan.isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <GreenPill icon={<div className="i-ph-heart text-[#8DD290] w-3 h-3"></div>}>Most popular</GreenPill>
                </div>
              )}

              {/* Price */}
              <div className="mb-6 mt-2">
                <div className="flex items-baseline justify-center gap-2 mb-2">
                  <span
                    className="text-black/30 text-2xl font-normal line-through"
                    style={{ fontFamily: 'Inter Display' }}
                  >
                    {currencySymbol}
                    {plan.originalPrice}
                  </span>
                  <span className="text-black text-4xl font-semibold" style={{ fontFamily: 'Inter Display' }}>
                    {currencySymbol}
                    {plan.price}
                  </span>
                </div>
                <p className="text-black/50 text-sm text-center" style={{ fontFamily: 'Inter Display' }}>
                  One-time payment
                </p>
              </div>

              {/* Messages Count - Highlighted */}
              <div className="mb-6">
                <div className="bg-[#8DD290]/10 border border-[#8DD290]/20 rounded-lg p-4 text-center">
                  <p className="text-black text-3xl font-bold mb-1" style={{ fontFamily: 'Inter Display' }}>
                    {plan.messages}
                  </p>
                  <p className="text-black/60 text-sm" style={{ fontFamily: 'Inter Display' }}>
                    Messages
                  </p>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 flex-grow mb-6">
                <li className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-[#8DD290] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Messages never expire</span>
                </li>
                <li className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-[#8DD290] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>No commitment</span>
                </li>
                <li className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-[#8DD290] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Instant activation</span>
                </li>
              </ul>

              {/* Purchase Button */}
              <button
                onClick={() => handlePurchase(plan.messages)}
                disabled={loadingButton === plan.messages}
                className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                  !plan.isPopular
                    ? 'bg-white text-black border border-black/50 shadow-[0_3px_0_0_rgba(141,144,144,0.5)] hover:shadow-[0_2px_0_0_rgba(141,144,144,0.7)] hover:translate-y-[1px] active:shadow-[0_1px_0_0_rgba(141,210,144,0.7)] active:translate-y-[2px]'
                    : 'bg-black text-white shadow-[0_3px_0_0_rgba(153,161,175,0.5)] hover:shadow-[0_2px_0_0_rgba(153,161,175,1)] hover:translate-y-[1px] active:shadow-[0_1px_0_0_rgba(153,161,175,1)] active:translate-y-[2px]'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                style={{ fontFamily: 'Inter Display' }}
              >
                {loadingButton === plan.messages ? (
                  <div className="i-ph-spinner animate-spin w-5 h-5" />
                ) : (
                  'Purchase Now'
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Info Card */}
        <div className="max-w-[1400px] mx-auto">
          <div className="bg-[#8DD290]/10 border border-[#8DD290]/20 rounded-xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#8DD290]/20 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-black text-lg font-semibold mb-1" style={{ fontFamily: 'Inter Display' }}>
                  Need more messages?
                </h3>
                <p className="text-black/60 text-sm" style={{ fontFamily: 'Inter Display' }}>
                  Looking for larger top-up packages? Contact our team for custom solutions.
                </p>
              </div>
            </div>
            <button
              onClick={handleContactClick}
              className="bg-black text-white py-3 px-8 rounded-xl font-semibold transition-all duration-200 whitespace-nowrap shadow-[0_3px_0_0_rgba(153,161,175,0.5)] hover:shadow-[0_2px_0_0_rgba(153,161,175,1)] hover:translate-y-[1px] active:shadow-[0_1px_0_0_rgba(153,161,175,1)] active:translate-y-[2px]"
              style={{ fontFamily: 'Inter Display' }}
            >
              Contact Sales
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
